import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { SecurityModule } from './modules/security/security.module';
import { CoreUIFlashToastModule, CoreUIToastsV2Module } from 'lib-klab-core-ui';

import { AUTH_UI_CONFIG, AuthUiConfig, LibKlabAuthUiModule } from 'lib-klab-auth-ui';
import { hasCustomerPortalAccess } from './modules/home/customer-iam-access.util';

import {
  AppActivityReportsPortAdapter,
  authBearerInterceptor,
  sessionRevokedInterceptor,
  CONNECTIVITY_REPORTS_PORT,
  ConnectivityReportsPortAdapter,
  DOCUMENT_ENV,
  CHAT_DIRECTORY_PORT,
  ChatDirectoryPortAdapter,
  EMPLOYEES_PORT,
  EmployeesPortAdapter,
  ENV,
  EXECUTABLE_VERSION_PORT,
  ExecutableVersionPortAdapter,
  KLAB_IAM_ENV,
  LOCATIONS_PORT,
  LocationsPortAdapter,
  MESSAGING_ENV,
  MESSAGING_PORT,
  MessagingPortAdapter,
  NOTIFICATIONS_ENV,
  ORG_ENV,
  PASSWORD_RECOVERY_PORT,
  PasswordRecoveryPortAdapter,
  REPORT_ENV,
  APP_REPORTS_PORT,
  SESSIONS_PORT,
  SessionsPortAdapter,
  TIMESHEET_PORT,
  KEYSTROKE_REPORTS_PORT,
  KeystrokeReportsPortAdapter,
  WebActivityReportsPortAdapter,
  WEB_REPORTS_PORT,
  EMPLOYEE_HIERARCHIES_PORT,
  EmployeeHierarchiesPortAdapter,
  ROLES_PORT,
  RolesPortAdapter,
  SessionRefreshOnFocusEffect,
  PEOPLE_ENV,
  REQUESTS_PORT,
  RequestsPortAdapter,
  EMPLOYEE_ENV,
  HR_ENV,
  AGREEMENT_ENV,
  PAYROLL_ENV,
  EMPLOYMENT_CONTRACTS_PORT,
  EmploymentContractsPortAdapter,
  EMPLOYMENT_AGREEMENTS_PORT,
  EmploymentAgreementsPortAdapter,
  STAFF_EMPLOYEES_PORT,
  StaffEmployeesPortAdapter,
  STAFF_VACATIONS_PORT,
  StaffVacationsPortAdapter,
  STAFF_BENEFITS_PORT,
  StaffBenefitsPortAdapter,
  STAFF_BENEFIT_REQUESTS_PORT,
  StaffBenefitRequestsPortAdapter,
  STAFF_PAYROLL_PORT,
  StaffPayrollPortAdapter,
  WORK_SCHEDULES_PORT,
  WorkSchedulesPortAdapter,
} from 'lib-klab-core';

import {
  IAM_BASE_PATH,
  ORG_BASE_PATH,
  PEOPLE_BASE_PATH,
  REPORT_BASE_PATH,
  STAFF_EMPLOYEE_BASE_PATH,
  STAFF_ORGANIZATION_BASE_PATH,
  STAFF_HR_BASE_PATH,
  STAFF_PAYROLL_BASE_PATH,
  STAFF_AGREEMENT_BASE_PATH,
} from 'lib-klab-sdks';
import { TimeSheetReportsPortAdapter } from 'lib-klab-core';
import { customerStaffMockFallbackInterceptor } from './core/http/customer-staff-mock-fallback.interceptor';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authBearerInterceptor, sessionRevokedInterceptor, customerStaffMockFallbackInterceptor]), withInterceptorsFromDi()),
    provideAnimations(),

    importProvidersFrom(SecurityModule, CoreUIToastsV2Module, CoreUIFlashToastModule, LibKlabAuthUiModule),

    { provide: ENV, useValue: environment },

    { provide: KLAB_IAM_ENV, useValue: { ...environment.micros.iam, validateEmptyMenu: true } },
    { provide: ORG_ENV, useValue: environment.micros.organization },
    { provide: REPORT_ENV, useValue: environment.micros.report },
    { provide: PEOPLE_ENV, useValue: environment.micros.people },
    { provide: MESSAGING_ENV, useValue: environment.micros.messaging },
    { provide: DOCUMENT_ENV, useValue: environment.micros.document },
    { provide: NOTIFICATIONS_ENV, useValue: environment.micros.notifications },
    { provide: EMPLOYEE_ENV, useValue: environment.micros.employee! },
    { provide: HR_ENV, useValue: environment.micros.hr! },
    { provide: PAYROLL_ENV, useValue: environment.micros.payroll! },
    { provide: AGREEMENT_ENV, useValue: environment.micros.agreement! },

    { provide: IAM_BASE_PATH, useValue: `${environment.micros.iam.host}${environment.micros.iam.apiBasePath}` },
    { provide: ORG_BASE_PATH, useValue: `${environment.micros.organization.host}${environment.micros.organization.apiBasePath}` },
    { provide: REPORT_BASE_PATH, useValue: `${environment.micros.report.host}${environment.micros.report.apiBasePath}` },
    { provide: PEOPLE_BASE_PATH, useValue: `${environment.micros.people.host}${environment.micros.people.apiBasePath}` },
    { provide: STAFF_EMPLOYEE_BASE_PATH, useValue: `${environment.micros.employee!.host}${environment.micros.employee!.apiBasePath}` },
    { provide: STAFF_ORGANIZATION_BASE_PATH, useValue: `${environment.micros.staffOrganization.host}${environment.micros.staffOrganization.apiBasePath}` },
    { provide: STAFF_HR_BASE_PATH, useValue: `${environment.micros.hr!.host}${environment.micros.hr!.apiBasePath}` },
    { provide: STAFF_PAYROLL_BASE_PATH, useValue: `${environment.micros.payroll!.host}${environment.micros.payroll!.apiBasePath}` },
    { provide: STAFF_AGREEMENT_BASE_PATH, useValue: `${environment.micros.agreement!.host}${environment.micros.agreement!.apiBasePath}` },

    { provide: SESSIONS_PORT, useExisting: SessionsPortAdapter },
    { provide: LOCATIONS_PORT, useExisting: LocationsPortAdapter },
    { provide: EXECUTABLE_VERSION_PORT, useExisting: ExecutableVersionPortAdapter },
    { provide: APP_REPORTS_PORT, useExisting: AppActivityReportsPortAdapter },
    { provide: WEB_REPORTS_PORT, useExisting: WebActivityReportsPortAdapter },
    { provide: CONNECTIVITY_REPORTS_PORT, useExisting: ConnectivityReportsPortAdapter },
    { provide: TIMESHEET_PORT, useExisting: TimeSheetReportsPortAdapter },
    { provide: KEYSTROKE_REPORTS_PORT, useExisting: KeystrokeReportsPortAdapter },
    { provide: PASSWORD_RECOVERY_PORT, useExisting: PasswordRecoveryPortAdapter },
    { provide: ROLES_PORT, useExisting: RolesPortAdapter },
    { provide: REQUESTS_PORT, useExisting: RequestsPortAdapter },
    { provide: CHAT_DIRECTORY_PORT, useExisting: ChatDirectoryPortAdapter },
    { provide: EMPLOYEES_PORT, useExisting: EmployeesPortAdapter },
    { provide: MESSAGING_PORT, useExisting: MessagingPortAdapter },
    { provide: WORK_SCHEDULES_PORT, useExisting: WorkSchedulesPortAdapter },
    { provide: STAFF_EMPLOYEES_PORT, useExisting: StaffEmployeesPortAdapter },
    { provide: EMPLOYMENT_CONTRACTS_PORT, useExisting: EmploymentContractsPortAdapter },
    { provide: EMPLOYMENT_AGREEMENTS_PORT, useExisting: EmploymentAgreementsPortAdapter },
    { provide: STAFF_VACATIONS_PORT, useExisting: StaffVacationsPortAdapter },
    { provide: STAFF_BENEFITS_PORT, useExisting: StaffBenefitsPortAdapter },
    { provide: STAFF_BENEFIT_REQUESTS_PORT, useExisting: StaffBenefitRequestsPortAdapter },
    { provide: STAFF_PAYROLL_PORT, useExisting: StaffPayrollPortAdapter },

    {
      provide: AUTH_UI_CONFIG,
      useValue: {
        appKind: 'customers',
        canAccessPortal: session => hasCustomerPortalAccess(session.menu),

        storageTokenKey: 'c2Vzc2lvBg',

        postLoginRedirectUrl: '/app',
        loginUrl: '/login',
        forbiddenRedirectUrl: '/login',

        redirectByRoleName: {
          Agente: '/app',
          Usuario: '/app',
        },
      } satisfies AuthUiConfig,
    },

    provideAppInitializer(() => {
      const effect = inject(SessionRefreshOnFocusEffect);
      effect.init();
      return firstValueFrom(effect.refreshIfLoggedIn(), { defaultValue: undefined }).catch(() => void 0);
    }),
  ],
};
